import { z } from 'zod';

import { STAGE_KINDS } from '@/lib/stage-kind';

// Contratos de Estudio de tiempos (so=420) validados con Zod: una respuesta con forma
// inesperada cae al error de su tarjeta, nunca a una excepción en el render. Los ids
// llegan como string (serializador de bigint).

export const AXES = ['person', 'documentType', 'processArea', 'department'] as const;
export type Axis = (typeof AXES)[number];
export const GRANULARITIES = ['day', 'month', 'year'] as const;
export type Granularity = (typeof GRANULARITIES)[number];
export const TABS = ['analisis', 'estandares'] as const;
/** Vista del cumplimiento de plazos: por colaborador o por departamento (2026-10-27). */
export const COMPLIANCE_VIEWS = ['persona', 'departamento'] as const;
export type ComplianceView = (typeof COMPLIANCE_VIEWS)[number];
export type Tab = (typeof TABS)[number];

export interface TimeStudyFilters {
  /** Fechas calendario `AAAA-MM-DD`, inclusivas, en el día de negocio de Costa Rica. */
  desde: string;
  hasta: string;
  tipo: string | null;
  area: string | null;
  eje: Axis;
  cumpl: ComplianceView;
  gran: Granularity;
  tab: Tab;
}

const seconds = z.number().int().nonnegative();
const named = z.object({ id: z.string(), name: z.string().nullable() });
const envelope = <T extends z.ZodType>(data: T) => z.object({ success: z.literal(true), data });

export const metricsSchema = z.object({
  workCount: seconds,
  stageCount: seconds,
  standardSeconds: seconds.nullable(),
  effortWithStandardSeconds: seconds,
  deviationSeconds: z.number().int().nullable(),
  deviationPct: z.number().nullable(),
  effortSeconds: seconds,
  /** Esfuerzo medio de las etapas con tiempo registrado (línea base de los estándares). */
  avgEffortSeconds: seconds.nullable().default(null),
  /** Etapas con tiempo registrado: el divisor del promedio. */
  measuredStageCount: seconds.default(0),
  reworkEffortSeconds: seconds,
  overStandardCount: seconds,
  withoutStandardCount: seconds,
});
export type Metrics = z.infer<typeof metricsSchema>;

export const deviationRowSchema = metricsSchema.extend({
  // G4: la fila de capacitación se mide por calendario (no esfuerzo).
  basis: z.literal('calendar').optional(),
  keys: z.object({
    person: named.nullable().optional(),
    stage: z.enum(['elaboracion', 'revision', 'aprobacion', 'capacitacion']).optional(),
    documentType: named.optional(),
    processArea: named.optional(),
    /** null = sin departamento conocido (reunión 2026-10-27). */
    department: named.nullable().optional(),
  }),
});
export type DeviationRow = z.infer<typeof deviationRowSchema>;

export const deviationResponseSchema = envelope(
  z.object({
    from: z.string(),
    to: z.string(),
    rows: z.array(deviationRowSchema),
    totals: metricsSchema,
  }),
);
export type Deviation = z.infer<typeof deviationResponseSchema>['data'];

export const reworkSummaryResponseSchema = envelope(
  z.object({
    totals: z.object({ reworkCount: seconds, effortSeconds: seconds }),
    people: z.array(
      z.object({
        userId: z.string().nullable(),
        name: z.string().nullable(),
        reworkCount: seconds,
        byRole: z.object({ elaborador: seconds, revisor: seconds }),
        documentCount: seconds,
        effortSeconds: seconds,
      }),
    ),
  }),
);
export type ReworkSummary = z.infer<typeof reworkSummaryResponseSchema>['data'];

export const throughputResponseSchema = envelope(
  z.object({
    granularity: z.enum(GRANULARITIES),
    buckets: z.array(z.object({ period: z.string(), started: seconds, completed: seconds })),
    totals: z.object({ started: seconds, completed: seconds }),
  }),
);
export type Throughput = z.infer<typeof throughputResponseSchema>['data'];

/** Series de Documentos por período: iniciados, o terminados en vigencia. */
export const THROUGHPUT_SERIES = ['started', 'completed'] as const;
export type ThroughputSeries = (typeof THROUGHPUT_SERIES)[number];

/** Un período y una serie de la tarjeta (G1-7): lo que abre el detalle. */
export interface PeriodPick {
  period: string;
  series: ThroughputSeries;
}

export const throughputDocumentsResponseSchema = envelope(
  z.object({
    granularity: z.enum(GRANULARITIES),
    period: z.string(),
    series: z.enum(THROUGHPUT_SERIES),
    from: z.string(),
    to: z.string(),
    total: seconds,
    items: z.array(
      z.object({
        flowId: z.string(),
        entryId: z.string(),
        code: z.string(),
        title: z.string().nullable(),
        kind: z.enum(['creacion', 'modificacion', 'carga_historica']),
        at: z.string(),
      }),
    ),
  }),
);
export type ThroughputDocuments = z.infer<typeof throughputDocumentsResponseSchema>['data'];

const complianceCounts = z.object({
  onTime: seconds,
  late: seconds,
  openOverdue: seconds,
  escalated: seconds,
  total: seconds,
  onTimePct: z.number().min(0).max(100).nullable(),
});

export const complianceResponseSchema = envelope(
  z.object({
    from: z.string(),
    to: z.string(),
    rows: z.array(
      complianceCounts.extend({
        person: z.object({ id: z.string(), name: z.string() }).nullable(),
      }),
    ),
    totals: complianceCounts,
  }),
);
export type Compliance = z.infer<typeof complianceResponseSchema>['data'];

export const departmentComplianceResponseSchema = envelope(
  z.object({
    from: z.string(),
    to: z.string(),
    rows: z.array(
      complianceCounts.extend({
        department: z.object({ id: z.number().int(), name: z.string() }).nullable(),
      }),
    ),
    totals: complianceCounts,
  }),
);
export type DepartmentCompliance = z.infer<typeof departmentComplianceResponseSchema>['data'];

export const myStagesResponseSchema = envelope(
  z.object({
    items: z.array(
      z.object({
        entryId: z.string(),
        code: z.string(),
        title: z.string().nullable(),
        flowInstanceId: z.string().nullable(),
        stageKind: z.enum(STAGE_KINDS),
        openedAt: z.string(),
        attempt: z.number().int().positive(),
        isReproceso: z.boolean(),
        macroSeconds: seconds,
        mine: z.object({ effortSeconds: seconds }),
      }),
    ),
  }),
);
export type MyStage = z.infer<typeof myStagesResponseSchema>['data']['items'][number];

export const STANDARD_STAGES = ['elaboracion', 'revision', 'aprobacion', 'capacitacion'] as const;
export type StandardStage = (typeof STANDARD_STAGES)[number];

const stageStandardSchema = z
  .object({
    versionId: z.string(),
    standardMinutes: z.number().int().positive().nullable(),
    effectiveFrom: z.string(),
  })
  .nullable();

export const standardsMatrixResponseSchema = envelope(
  z.array(
    z.object({
      documentTypeId: z.string(),
      documentTypeName: z.string(),
      flowKind: z.enum(['creacion', 'modificacion']),
      stages: z.object({
        elaboracion: stageStandardSchema,
        revision: stageStandardSchema,
        aprobacion: stageStandardSchema,
        capacitacion: stageStandardSchema,
      }),
      totalMinutes: z.number().int().nonnegative().nullable(),
    }),
  ),
);
export type StandardsRow = z.infer<typeof standardsMatrixResponseSchema>['data'][number];

export const historyPageSchema = z.object({
  success: z.literal(true),
  data: z.array(
    z.object({
      versionId: z.string(),
      stageKind: z.enum(STANDARD_STAGES),
      standardMinutes: z.number().int().positive().nullable(),
      effectiveFrom: z.string(),
      reason: z.string(),
      createdByName: z.string().nullable(),
    }),
  ),
  meta: z.object({ next_cursor: z.string().nullable() }),
});
export type HistoryItem = z.infer<typeof historyPageSchema>['data'][number];

export type FlowKind = StandardsRow['flowKind'];
